import { FC, HTMLAttributes, useMemo } from 'react';

import styles from './TableTotal.module.css';

type SingleTotalType = {
  total: number;
  itemHeight: number;
};
type MultiTotalType = {
  /** The size of every item in the list. */
  sizes: number[];
};

type TableTotalType = HTMLAttributes<HTMLDivElement> & {
  collapsed?: boolean;
  total?: number;
  /** The current height of the table */
  height?: number;
  sizes?: number[];
  itemHeight?: number;
} & (SingleTotalType | MultiTotalType);
const TableTotal: FC<TableTotalType> = ({
  collapsed = false,
  total = 0,
  height = 232 - 24 - 24,
  itemHeight = undefined,
  sizes = [],
  ...divProps
}) => {
  /**
   * Handles either dynamic sizes or creates a similar approach if only total and itemHeight are provided.
   */
  const rowTotal = useMemo<number>(() => {
    if (collapsed && (!sizes.length || !itemHeight)) return 0;
    let deductedHeight = height;
    let itemsInView = 0;
    const allSizes = sizes.length ? sizes : new Array(total).fill(itemHeight || 0);
    for (let i = 0; i < allSizes.length; i++) {
      // Partially in view counts as if in view, advance the algo if it needs more logic like 50% in view.
      const itemSize = allSizes[i];
      deductedHeight -= itemSize;
      if (deductedHeight <= 0) {
        // Check if more than 50% is in view
        const fiftyCent = deductedHeight + itemSize; // undo
        if (fiftyCent - itemSize / 2 <= 0) break;
      }
      itemsInView++;
    }
    return allSizes.length - itemsInView;
  }, [collapsed, sizes, height, total, itemHeight]);

  return (
    <div data-testid={`rowColumnTotal`} className={styles.rowTotalStyle} {...divProps}>
      {rowTotal > 0 && (
        <>
          {!collapsed && '+'}
          {!collapsed && <>{rowTotal}</>}
        </>
      )}
    </div>
  );
};
export default TableTotal;
